import { blockToTableData } from "@/components/editor/table-grid/tableModel";
import { tileText } from "./tableTiles";
import type { TableSummary } from "./useTables";

/**
 * Finding a table by what is in it.
 *
 * Authors remember "the one with ResNet in it" more often than its caption,
 * and captions are often empty. So a query matches the name, the label, the
 * column headers **and the cells**. `GET /api/tables` already returns every
 * table's content (up to 200), so this runs in the browser (ANSWERS-from-lilia
 * §1).
 *
 * A match says where it was found, so the preview can light up the cell.
 */

export type TableMatch =
  | { kind: "name" }
  | { kind: "label" }
  | { kind: "column"; c: number }
  | { kind: "cell"; r: number; c: number };

export interface TableHit {
  table: TableSummary;
  /** Null when there is no query: everything is a hit. */
  match: TableMatch | null;
}

const norm = (s: string) => s.toLowerCase().replace(/[\\${}]/g, "");

/** Where `q` is found in `t`, best place first: name, label, columns, cells. */
export function matchTable(t: TableSummary, query: string): TableMatch | null {
  const q = norm(query.trim());
  if (!q) return null;
  if (norm(t.caption || "untitled").includes(q)) return { kind: "name" };
  if (t.label && norm(t.label).includes(q)) return { kind: "label" };
  let text;
  try {
    text = tileText(blockToTableData(t.content as never));
  } catch {
    return null;
  }
  const col = text.header?.findIndex((h) => norm(h).includes(q)) ?? -1;
  if (col >= 0) return { kind: "column", c: col };
  for (let r = 0; r < text.rows.length; r++) {
    const c = text.rows[r].findIndex((v) => norm(v).includes(q));
    if (c >= 0) return { kind: "cell", r, c };
  }
  return null;
}

/** The tables `query` finds, in their given order. */
export function searchTables(tables: TableSummary[], query: string): TableHit[] {
  if (!query.trim()) return tables.map((table) => ({ table, match: null }));
  const out: TableHit[] = [];
  for (const table of tables) {
    const match = matchTable(table, query);
    if (match) out.push({ table, match });
  }
  return out;
}
